/**
 * 产品规格对比组件（theme block 版） - 交互逻辑
 *
 * 架构说明：
 *   - 父 block 输出 <div data-cpsc-root data-config-*> 作为根容器
 *   - 子 block 输出 <div data-cpsc-column> / <div data-cpsc-spec-row>，
 *     所有 settings 以 data-* 属性编码；CSS 强制隐藏这些裸数据 div
 *   - 本文件扫描每个 [data-cpsc-root]：
 *       1) 解析 config + columns + specRows（DOM 替代 block.blocks）
 *       2) 在根容器内生成可视 UI（列头 / 图片 / 按钮 / 规格分组）
 *       3) 把子 block 的 data-shopify-* 属性复制到对应可视元素上，
 *          保证 theme editor 选中 block 时能正确高亮
 *       4) 绑定下拉选择器 / 列切换 / 去重交互
 *
 * 事件：
 *   - DOMContentLoaded / shopify:section:load / shopify:block:load / shopify:block:reorder
 *     → 重新初始化所有 root（旧实例 destroy，新实例创建）
 *   - 全局 click → 关闭所有 dropdown
 */

const ARROW_DOWN_SVG =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M8.39961 10.7999L11.9996 14.3999L15.5996 10.7999" stroke="#161616" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/>' +
  "</svg>";

const SHOPIFY_ATTR_PREFIX = "data-shopify-";

class ComparisonComponent {
  constructor(rootEl) {
    this.root = rootEl;
    this.mount = rootEl.querySelector(":scope > [data-cpsc-mount]");
    if (!this.mount) return;

    this.config = this._parseConfig();
    this.columns = this._parseColumns();
    this.specRows = this._parseSpecRows();

    this.pcDisplay = Math.min(this.columns.length, this.config.pcMax);
    this.mobileDisplay = Math.min(this.columns.length, this.config.mobileMax);
    this.pcNeedSelector = this.columns.length > this.config.pcMax;
    this.mobileNeedSelector = this.columns.length > this.config.mobileMax;

    this.maxDisplay = Math.max(this.pcDisplay, this.mobileDisplay);
    this.selectedIds = Array.from({ length: this.maxDisplay }, (_, i) => {
      const col = this.columns[i];
      return col ? col.blockId : null;
    });

    this.render();
    this.bindEvents();
  }

  destroy() {
    if (this.ui && this.ui.parentNode) this.ui.parentNode.removeChild(this.ui);
  }

  _parseConfig() {
    const d = this.root.dataset;
    return {
      title: d.configTitle || "",
      bgColor: d.configBgColor || "#FFFFFF",
      columnCount: parseInt(d.configColumnCount || "3", 10) || 3,
      seeMoreText: d.configSeeMoreText || "",
      seeMoreLink: d.configSeeMoreLink || "",
      buyNowLabel: d.configBuyNowLabel || "Buy Now",
      learnMoreLabel: d.configLearnMoreLabel || "Learn More",
      pcMax: parseInt(d.configPcMax || "3", 10) || 3,
      mobileMax: parseInt(d.configMobileMax || "2", 10) || 2,
    };
  }

  _parseColumns() {
    const nodes = this.mount.querySelectorAll(":scope > [data-cpsc-column]");
    return Array.from(nodes).map((node, i) => ({
      node,
      index: i,
      blockId: node.dataset.blockId || "",
      title: node.dataset.title || "",
      productSort: node.dataset.productSort || "",
      imagePc: node.dataset.imagePc || "",
      imageMobile: node.dataset.imageMobile || "",
      buttonLink: node.dataset.buttonLink || "",
      textButtonLink: node.dataset.textButtonLink || "",
      buttonBgColor: node.dataset.buttonBgColor || "#161616",
      buttonFontColor: node.dataset.buttonFontColor || "#ffffff",
      openChannelModal: node.dataset.openChannelModal === "true",
      shopifyAttrs: this._extractShopifyAttrs(node),
    }));
  }

  _parseSpecRows() {
    const nodes = this.mount.querySelectorAll(":scope > [data-cpsc-spec-row]");
    return Array.from(nodes).map((node) => {
      const cellNodes = node.querySelectorAll(":scope > [data-cpsc-cell]");
      const cells = Array.from(cellNodes)
        .map((cell) => ({
          colIndex: parseInt(cell.dataset.colIndex, 10),
          icon: cell.dataset.icon || "",
          text: cell.dataset.text || "",
        }))
        .filter((c) => !Number.isNaN(c.colIndex))
        .sort((a, b) => a.colIndex - b.colIndex);
      return {
        node,
        blockId: node.dataset.blockId || "",
        groupTitle: node.dataset.groupTitle || "",
        cells,
        shopifyAttrs: this._extractShopifyAttrs(node),
      };
    });
  }

  _extractShopifyAttrs(node) {
    const out = [];
    Array.from(node.attributes).forEach((attr) => {
      if (attr.name.startsWith(SHOPIFY_ATTR_PREFIX)) {
        out.push([attr.name, attr.value]);
      }
    });
    return out;
  }

  _applyShopifyAttrs(el, attrs) {
    attrs.forEach(([k, v]) => el.setAttribute(k, v));
  }

  _isEditor() {
    return (
      typeof window !== "undefined" &&
      window.Shopify &&
      window.Shopify.designMode === true
    );
  }

  _columnsAt(device) {
    const display = device === "pc" ? this.pcDisplay : this.mobileDisplay;
    const result = [];
    for (let pos = 0; pos < display; pos++) {
      const id = this.selectedIds[pos];
      const col =
        this.columns.find((c) => c.blockId === id) || this.columns[pos] || null;
      if (col) result.push(col);
    }
    return result;
  }

  _getSortGroups() {
    const seen = new Set();
    const out = [];
    this.columns.forEach((c) => {
      if (c.productSort && !seen.has(c.productSort)) {
        seen.add(c.productSort);
        out.push(c.productSort);
      }
    });
    return out;
  }

  render() {
    const prev = this.mount.querySelector(":scope > .cpsc-ui");
    if (prev) prev.remove();

    const ui = document.createElement("div");
    ui.className = "cpsc-ui";

    if (this.config.title) {
      const h2 = document.createElement("h2");
      h2.className = "cpsc-title";
      h2.textContent = this.config.title;
      ui.appendChild(h2);
    }

    if (this.columns.length === 0) {
      if (this._isEditor()) {
        const hint = document.createElement("p");
        hint.className = "cpsc-empty-hint";
        hint.textContent = "请先添加「产品列」block，然后按需添加「规格行」。";
        ui.appendChild(hint);
      }
    } else {
      ui.appendChild(this._renderColumnHeaders("pc"));
      ui.appendChild(this._renderColumnHeaders("mobile"));
      ui.appendChild(this._renderImages("pc"));
      ui.appendChild(this._renderImages("mobile"));
      ui.appendChild(this._renderButtons("pc"));
      ui.appendChild(this._renderButtons("mobile"));
      ui.appendChild(this._renderSpecs());
    }

    if (this.config.seeMoreText && this.config.seeMoreLink) {
      const wrap = document.createElement("div");
      wrap.className = "cpsc-see-more";
      const a = document.createElement("a");
      a.className = "ro-btn cpsc-see-more-button size-l theme-light";
      a.href = this.config.seeMoreLink;
      a.textContent = this.config.seeMoreText;
      wrap.appendChild(a);
      ui.appendChild(wrap);
    }

    this.mount.appendChild(ui);
    this.ui = ui;
  }

  _renderColumnHeaders(device) {
    const display = device === "pc" ? this.pcDisplay : this.mobileDisplay;
    const needSelector =
      device === "pc" ? this.pcNeedSelector : this.mobileNeedSelector;
    const wrap = document.createElement("div");
    wrap.className = `cpsc-columns-header cpsc-row cpsc-row-${device} cpsc-grid-${display}`;

    this._columnsAt(device).forEach((col, pos) => {
      const colEl = document.createElement("div");
      colEl.className = "cpsc-column";
      colEl.dataset.colPosition = String(pos);
      colEl.dataset.colId = col.blockId;
      colEl.dataset.device = device;
      this._applyShopifyAttrs(colEl, col.shopifyAttrs);

      if (needSelector) {
        colEl.appendChild(this._renderSelector(col, pos, device));
      } else {
        const titleWrap = document.createElement("div");
        titleWrap.className = "cpsc-fixed-title-wrap";
        const h4 = document.createElement("h4");
        h4.className = "cpsc-fixed-title";
        h4.textContent = col.title;
        titleWrap.appendChild(h4);
        colEl.appendChild(titleWrap);
      }

      wrap.appendChild(colEl);
    });
    return wrap;
  }

  _renderSelector(currentCol, position, device) {
    const wrap = document.createElement("div");
    wrap.className = `cpsc-selector cpsc-selector-${device}`;
    wrap.dataset.position = String(position);
    wrap.dataset.device = device;
    wrap.dataset.selectedId = currentCol.blockId;

    const trigger = document.createElement("div");
    trigger.className = "cpsc-selector-trigger";
    const name = document.createElement("span");
    name.className = "cpsc-selected-name";
    name.textContent = currentCol.title || "Select Product";
    trigger.appendChild(name);
    const arrow = document.createElement("span");
    arrow.className = "cpsc-dropdown-arrow";
    arrow.innerHTML = ARROW_DOWN_SVG;
    trigger.appendChild(arrow);
    wrap.appendChild(trigger);

    const dropdown = document.createElement("div");
    dropdown.className = "cpsc-dropdown down";

    const sortGroups = this._getSortGroups();
    if (sortGroups.length > 0) {
      sortGroups.forEach((sortKey) => {
        const group = document.createElement("div");
        group.className = "cpsc-product-group";

        const header = document.createElement("div");
        header.className = "cpsc-group-header active";
        header.dataset.group = sortKey;
        const gname = document.createElement("span");
        gname.className = "cpsc-group-name";
        gname.textContent = sortKey.replace(/-/g, " ");
        header.appendChild(gname);
        const garrow = document.createElement("span");
        garrow.className = "cpsc-group-arrow";
        garrow.innerHTML = ARROW_DOWN_SVG;
        header.appendChild(garrow);
        group.appendChild(header);

        const options = document.createElement("div");
        options.className = "cpsc-options";
        options.dataset.groupOptions = sortKey;
        options.style.maxHeight = "1000px";
        this.columns
          .filter((c) => c.productSort === sortKey)
          .forEach((c) =>
            options.appendChild(this._renderOption(c, currentCol.blockId)),
          );
        group.appendChild(options);

        dropdown.appendChild(group);
      });
    } else {
      const group = document.createElement("div");
      group.className = "cpsc-product-group";
      const options = document.createElement("div");
      options.className = "cpsc-options";
      options.style.maxHeight = "1000px";
      this.columns.forEach((c) =>
        options.appendChild(this._renderOption(c, currentCol.blockId)),
      );
      group.appendChild(options);
      dropdown.appendChild(group);
    }

    wrap.appendChild(dropdown);
    return wrap;
  }

  _renderOption(col, currentId) {
    const opt = document.createElement("div");
    opt.className =
      "cpsc-product-option" + (col.blockId === currentId ? " selected" : "");
    opt.dataset.productId = col.blockId;
    opt.dataset.productTitle = col.title;
    opt.textContent = col.title;
    return opt;
  }

  _renderImages(device) {
    const display = device === "pc" ? this.pcDisplay : this.mobileDisplay;
    const imgKey = device === "pc" ? "imagePc" : "imageMobile";
    const size = device === "pc" ? 216 : 160;
    const wrap = document.createElement("div");
    wrap.className = `cpsc-images cpsc-row cpsc-row-${device} cpsc-grid-${display}`;

    this._columnsAt(device).forEach((col, pos) => {
      const cell = document.createElement("div");
      cell.className = "cpsc-cell cpsc-cell-image";
      cell.dataset.colPosition = String(pos);
      cell.dataset.colId = col.blockId;
      cell.dataset.device = device;
      this._applyShopifyAttrs(cell, col.shopifyAttrs);

      if (col[imgKey]) {
        const img = document.createElement("img");
        img.className = "cpsc-product-img";
        img.src = col[imgKey];
        // robo-AMP291：列头已有产品名，产品图作装饰
        img.alt = "";
        img.loading = "lazy";
        img.width = size;
        img.height = size;
        cell.appendChild(img);
      }
      wrap.appendChild(cell);
    });
    return wrap;
  }

  _renderButtons(device) {
    const display = device === "pc" ? this.pcDisplay : this.mobileDisplay;
    const wrap = document.createElement("div");
    wrap.className = `cpsc-buttons cpsc-row cpsc-row-${device} cpsc-grid-${display}`;

    this._columnsAt(device).forEach((col, pos) => {
      const cell = document.createElement("div");
      cell.className = "cpsc-cell cpsc-cell-buttons";
      cell.dataset.colPosition = String(pos);
      cell.dataset.colId = col.blockId;
      cell.dataset.device = device;
      this._applyShopifyAttrs(cell, col.shopifyAttrs);

      const inner = document.createElement("div");
      inner.className = "compare-buttons";

      if (col.openChannelModal) {
        const buy = document.createElement("button");
        buy.type = "button";
        buy.className =
          "ro-btn size-l customed show-na-compare-channel-modal";
        buy.style.backgroundColor = col.buttonBgColor;
        buy.style.color = col.buttonFontColor;
        buy.dataset.columnIndex = String(col.index);
        buy.textContent = this.config.buyNowLabel;
        inner.appendChild(buy);
      } else if (col.buttonLink) {
        const buy = document.createElement("a");
        buy.className = "ro-btn size-l customed";
        buy.href = col.buttonLink;
        buy.style.backgroundColor = col.buttonBgColor;
        buy.style.color = col.buttonFontColor;
        buy.textContent = this.config.buyNowLabel;
        inner.appendChild(buy);
      }
      if (col.textButtonLink) {
        const text = document.createElement("a");
        text.className = "ro-btn theme-text size-m";
        text.href = col.textButtonLink;
        text.textContent = this.config.learnMoreLabel;
        inner.appendChild(text);
      }

      cell.appendChild(inner);
      wrap.appendChild(cell);
    });
    return wrap;
  }

  _renderSpecs() {
    const wrap = document.createElement("div");
    wrap.className = "cpsc-specs";

    let currentGroup = null;
    let currentGroupTitle = null;
    let first = true;

    this.specRows.forEach((row) => {
      if (first || row.groupTitle !== currentGroupTitle) {
        currentGroup = document.createElement("div");
        currentGroup.className = "cpsc-group";
        if (row.groupTitle) {
          const h3 = document.createElement("h3");
          h3.className = "cpsc-group-title";
          h3.textContent = row.groupTitle;
          currentGroup.appendChild(h3);
        }
        wrap.appendChild(currentGroup);
        currentGroupTitle = row.groupTitle;
        first = false;
      }

      const rowEl = document.createElement("div");
      rowEl.className = "cpsc-spec-row";
      rowEl.dataset.blockId = row.blockId;
      this._applyShopifyAttrs(rowEl, row.shopifyAttrs);

      rowEl.appendChild(this._renderSpecRowGrid(row, "pc"));
      rowEl.appendChild(this._renderSpecRowGrid(row, "mobile"));

      currentGroup.appendChild(rowEl);
    });

    return wrap;
  }

  _renderSpecRowGrid(row, device) {
    const display = device === "pc" ? this.pcDisplay : this.mobileDisplay;
    const grid = document.createElement("div");
    grid.className = `cpsc-row cpsc-row-${device} cpsc-grid-${display}`;

    this._columnsAt(device).forEach((col, pos) => {
      const cell = document.createElement("div");
      cell.className = "cpsc-cell cpsc-cell-spec";
      cell.dataset.colPosition = String(pos);

      const targetColIndex = col.index + 1;
      const data = row.cells.find((c) => c.colIndex === targetColIndex);
      if (data) {
        if (data.icon) {
          const img = document.createElement("img");
          img.className = "cpsc-spec-icon";
          img.src = data.icon;
          img.alt = "";
          img.loading = "lazy";
          cell.appendChild(img);
        }
        const text = document.createElement("div");
        text.className = "cpsc-spec-text";
        text.innerHTML = data.text;
        cell.appendChild(text);
      }
      grid.appendChild(cell);
    });
    return grid;
  }

  bindEvents() {
    if (!this.ui) return;

    this.ui.querySelectorAll(".cpsc-selector").forEach((selector) => {
      const trigger = selector.querySelector(".cpsc-selector-trigger");
      if (trigger) {
        trigger.addEventListener("click", (e) => {
          e.stopPropagation();
          this._toggleSelector(selector);
        });
      }

      selector.querySelectorAll(".cpsc-group-header").forEach((header) => {
        header.addEventListener("click", (e) => {
          e.stopPropagation();
          header.classList.toggle("active");
        });
      });

      selector.querySelectorAll(".cpsc-product-option").forEach((opt) => {
        opt.addEventListener("click", (e) => {
          e.stopPropagation();
          const id = opt.dataset.productId;
          const pos = parseInt(selector.dataset.position, 10);
          if (id && !Number.isNaN(pos)) this._selectProduct(pos, id);
        });
      });

      let leaveTimer = null;
      selector.addEventListener("mouseleave", () => {
        leaveTimer = setTimeout(() => this._closeSelector(selector), 300);
      });
      selector.addEventListener("mouseenter", () => {
        if (leaveTimer) {
          clearTimeout(leaveTimer);
          leaveTimer = null;
        }
      });
    });
  }

  _toggleSelector(selector) {
    this.ui.querySelectorAll(".cpsc-selector.active").forEach((s) => {
      if (s !== selector) s.classList.remove("active");
    });
    const wasActive = selector.classList.contains("active");
    selector.classList.toggle("active");
    if (!wasActive && selector.classList.contains("active")) {
      this._setDropdownDirection(selector);
    }
  }

  _closeSelector(selector) {
    selector.classList.remove("active");
  }

  _setDropdownDirection(selector) {
    const trigger = selector.querySelector(".cpsc-selector-trigger");
    const dropdown = selector.querySelector(".cpsc-dropdown");
    if (!trigger || !dropdown) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const threshold = 376;
    dropdown.classList.remove("up", "down");
    dropdown.classList.add(spaceBelow >= threshold ? "down" : "up");
  }

  _selectProduct(position, blockId) {
    const col = this.columns.find((c) => c.blockId === blockId);
    if (!col) return;

    const dupIdx = this.selectedIds.indexOf(blockId);
    if (dupIdx !== -1 && dupIdx !== position) {
      const unused = this.columns.find(
        (c) => !this.selectedIds.includes(c.blockId),
      );
      const currentAtPos = this.columns.find(
        (c) => c.blockId === this.selectedIds[position],
      );
      const fallback = unused || currentAtPos;
      if (fallback) {
        this.selectedIds[dupIdx] = fallback.blockId;
      }
    }
    this.selectedIds[position] = blockId;

    this.render();
    this.bindEvents();
  }
}

function initAll() {
  document.querySelectorAll("[data-cpsc-root]").forEach((root) => {
    if (root.__cpscInstance) {
      root.__cpscInstance.destroy();
    }
    try {
      root.__cpscInstance = new ComparisonComponent(root);
    } catch (err) {
      console.error("[cpsc] init failed:", err);
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}

document.addEventListener("shopify:section:load", initAll);
document.addEventListener("shopify:block:load", initAll);
document.addEventListener("shopify:block:reorder", initAll);

if (!window.__cpscGlobalBound) {
  window.__cpscGlobalBound = true;

  document.addEventListener("click", (e) => {
    document.querySelectorAll(".cpsc .cpsc-selector.active").forEach((s) => {
      if (!s.contains(e.target)) s.classList.remove("active");
    });
  });

  let resizeTimer = null;
  window.addEventListener("resize", () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document
        .querySelectorAll(".cpsc .cpsc-selector.active")
        .forEach((s) => s.classList.remove("active"));
    }, 200);
  });
}
